import { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Send, Loader2, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { CONTACTS, waLink } from "@/lib/contact";
import { toast } from "sonner";

const WhatsAppIcon = ({ size = 18, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.5 14.4c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51-.17-.01-.37-.01-.57-.01-.2 0-.52.07-.8.37-.27.3-1.05 1.02-1.05 2.5 0 1.47 1.08 2.9 1.23 3.1.15.2 2.12 3.24 5.14 4.54.72.31 1.28.5 1.71.64.72.23 1.38.2 1.9.12.58-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35zM12.05 21.5h-.01a9.5 9.5 0 0 1-4.83-1.32l-.35-.2-3.59.94.96-3.5-.23-.36a9.46 9.46 0 0 1-1.45-5.05c0-5.24 4.27-9.5 9.52-9.5 2.54 0 4.93.99 6.72 2.79a9.44 9.44 0 0 1 2.78 6.72c0 5.24-4.27 9.5-9.52 9.5zm8.1-17.6A11.38 11.38 0 0 0 12.05.5C5.76.5.64 5.62.64 11.9c0 2.02.53 3.99 1.53 5.73L.5 23.5l6.03-1.58a11.36 11.36 0 0 0 5.51 1.4h.01c6.28 0 11.4-5.12 11.4-11.4 0-3.05-1.19-5.91-3.35-8.06z" />
  </svg>
);

const SUGGESTIONS = [
  "What does ThinkStack do?",
  "Help with AI governance & PDPA",
  "AI for supply chain",
];

const GREETING =
  "Hi! I'm ThinkStack's AI assistant. I can explain how we help enterprises across Singapore and APAC put AI into production — from consulting and governance to automation and custom builds. What are you working on? I can also connect you with Raman on WhatsApp.";

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("chat"); // chat | handoff
  const [lead, setLead] = useState({ name: "", phone: "" });
  const [savingLead, setSavingLead] = useState(false);
  const sessionId = useRef(
    (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
      `web-${Date.now()}`
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open, view]);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("ts_nudge_seen")) return;
    } catch (e) {}
    const t = setTimeout(() => {
      if (!open) setNudge(true);
    }, 15000);
    return () => clearTimeout(t);
  }, [open]);

  const openChat = () => {
    setOpen(true);
    setNudge(false);
    try { sessionStorage.setItem("ts_nudge_seen", "1"); } catch (e) {}
  };
  const dismissNudge = () => {
    setNudge(false);
    try { sessionStorage.setItem("ts_nudge_seen", "1"); } catch (e) {}
  };

  const lastUserTopic = () => {
    const u = [...messages].reverse().find((m) => m.role === "user");
    return u ? u.content.slice(0, 160) : "";
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    const next = [...messages, { role: "user", content: msg }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/chat", {
        session_id: sessionId.current,
        messages: next.slice(-14),
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry, I couldn't respond just now. Tap ‘Talk to a human on WhatsApp’ to reach Raman directly.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const submitHandoff = async (e) => {
    e.preventDefault();
    if (!lead.name.trim() || lead.phone.trim().length < 3) {
      toast.error("Please add your name and mobile number.");
      return;
    }
    setSavingLead(true);
    const topic = lastUserTopic();
    try {
      await api.post("/assistant/lead", {
        name: lead.name,
        phone: lead.phone,
        session_id: sessionId.current,
        message: topic ? `From AI assistant. Interested in: ${topic}` : "Requested a WhatsApp callback via the AI assistant.",
      });
    } catch (err) {
      // still allow WhatsApp handover even if save fails
    } finally {
      setSavingLead(false);
    }
    const text = `Hi Raman, I'm ${lead.name} (${lead.phone}). I was exploring thinkstack.sg${
      topic ? ` and I'm interested in: ${topic}` : ""
    }. Could we connect?`;
    window.open(waLink(CONTACTS.raman.wa, text), "_blank", "noopener");
    toast.success("Thanks! Opening WhatsApp to connect you with Raman.");
    setView("chat");
    setLead({ name: "", phone: "" });
    setMessages((m) => [
      ...m,
      { role: "assistant", content: "Great — I've shared your details with Raman and opened WhatsApp so you can say hello. Anything else I can help with meanwhile?" },
    ]);
  };

  const directWa = waLink(
    CONTACTS.raman.wa,
    "Hi ThinkStack — I'd like to speak with your team about enterprise AI."
  );

  return (
    <>
      {/* Floating buttons */}
      {!open && (
        <div className="fixed bottom-5 right-5 z-[70] flex flex-col items-end gap-3" data-testid="floater-stack">
          {nudge && (
            <div
              data-testid="chat-nudge"
              className="relative mb-1 max-w-[250px] bg-white border border-[#E5E5E5] shadow-xl shadow-black/10 rounded-xl rounded-br-sm px-4 py-3 animate-in fade-in slide-in-from-bottom-2 duration-300"
            >
              <button
                onClick={dismissNudge}
                data-testid="chat-nudge-close"
                aria-label="Dismiss"
                className="absolute -top-2.5 -right-2.5 h-6 w-6 rounded-full bg-deep-navy text-white flex items-center justify-center shadow-md hover:bg-restrained-blue transition-colors"
              >
                <X size={13} />
              </button>
              <button onClick={openChat} data-testid="chat-nudge-open" className="text-left block">
                <p className="text-sm text-charcoal leading-snug">
                  Exploring AI for your business? I can answer questions or connect you with Raman.
                </p>
                <span className="mt-1.5 inline-block text-xs font-medium text-restrained-blue">
                  Chat with our AI →
                </span>
              </button>
            </div>
          )}
          <a
            href={directWa}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="whatsapp-floater"
            className="group flex items-center gap-2 rounded-full bg-[#25D366] text-white pl-4 pr-5 py-3 shadow-lg shadow-black/20 hover:brightness-105 transition-all"
          >
            <WhatsAppIcon size={20} />
            <span className="text-sm font-medium">WhatsApp</span>
          </a>
          <button
            onClick={openChat}
            data-testid="chat-open-button"
            className="group flex items-center gap-2 rounded-full bg-deep-navy text-white pl-4 pr-5 py-3 shadow-lg shadow-black/25 hover:bg-restrained-blue transition-all"
          >
            <MessageSquare size={20} />
            <span className="text-sm font-medium">Chat with AI</span>
          </button>
        </div>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-5 right-5 z-[70] w-[calc(100vw-2.5rem)] sm:w-[384px] h-[560px] max-h-[calc(100vh-2.5rem)] flex flex-col bg-white border border-[#E5E5E5] shadow-2xl shadow-black/25 rounded-xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-deep-navy text-white px-4 py-3.5 flex items-center gap-3">
            {view === "handoff" && (
              <button onClick={() => setView("chat")} data-testid="handoff-back" className="text-white/80 hover:text-white">
                <ArrowLeft size={18} />
              </button>
            )}
            <div className="h-9 w-9 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "#5C2D50" }}>
              <MessageSquare size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold leading-tight">ThinkStack Assistant</p>
              <p className="text-[11px] text-white/60 leading-tight">AI · replies instantly · Singapore</p>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close-button" className="text-white/80 hover:text-white">
              <X size={20} />
            </button>
          </div>

          {view === "chat" ? (
            <>
              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-off-white/40" data-testid="chat-messages">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed rounded-2xl ${
                        m.role === "user"
                          ? "bg-deep-navy text-white rounded-br-sm"
                          : "bg-white border border-[#E9E9E9] text-charcoal rounded-bl-sm"
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-[#E9E9E9] rounded-2xl rounded-bl-sm px-4 py-3">
                      <Loader2 className="animate-spin text-charcoal/50" size={16} />
                    </div>
                  </div>
                )}
                {messages.length <= 1 && !loading && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        data-testid="chat-suggestion"
                        className="text-xs border border-[#D9D9D9] text-charcoal/80 bg-white px-3 py-1.5 rounded-full hover:border-deep-navy hover:text-deep-navy transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Input */}
              <div className="border-t border-[#EEE] px-3 pt-3 pb-2 bg-white">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    send();
                  }}
                  className="flex items-end gap-2"
                >
                  <textarea
                    data-testid="chat-input"
                    rows={1}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    placeholder="Ask about ThinkStack…"
                    className="flex-1 resize-none max-h-24 border border-[#E0E0E0] rounded-lg px-3 py-2.5 text-sm text-charcoal placeholder:text-charcoal/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-restrained-blue"
                  />
                  <button
                    type="submit"
                    disabled={loading || !input.trim()}
                    data-testid="chat-send-button"
                    className="h-[42px] w-[42px] shrink-0 flex items-center justify-center bg-deep-navy text-white rounded-lg hover:bg-restrained-blue transition-colors disabled:opacity-40"
                  >
                    <Send size={17} />
                  </button>
                </form>
                <button
                  onClick={() => setView("handoff")}
                  data-testid="handoff-open-button"
                  className="mt-2 w-full flex items-center justify-center gap-2 text-[#25D366] text-sm font-medium py-1.5 hover:underline"
                >
                  <WhatsAppIcon size={16} /> Talk to a human on WhatsApp
                </button>
              </div>
            </>
          ) : (
            /* Handoff / lead capture */
            <div className="flex-1 overflow-y-auto p-5 bg-white" data-testid="handoff-view">
              <h3 className="font-serif text-xl text-deep-navy">Connect with Raman</h3>
              <p className="text-sm text-charcoal/70 mt-2 leading-relaxed">
                Share your name and mobile number. We'll note your interest and open WhatsApp so you can speak with Raman, our co-founder, directly.
              </p>
              <form onSubmit={submitHandoff} className="mt-5 space-y-4">
                <div>
                  <label className="overline text-charcoal/60 block mb-1.5" htmlFor="hf-name">Your name *</label>
                  <input
                    id="hf-name"
                    data-testid="handoff-name"
                    value={lead.name}
                    onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))}
                    placeholder="Jane Tan"
                    className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-restrained-blue"
                  />
                </div>
                <div>
                  <label className="overline text-charcoal/60 block mb-1.5" htmlFor="hf-phone">Mobile / WhatsApp number *</label>
                  <input
                    id="hf-phone"
                    data-testid="handoff-phone"
                    value={lead.phone}
                    onChange={(e) => setLead((l) => ({ ...l, phone: e.target.value }))}
                    placeholder="+65 9xxx xxxx"
                    className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-restrained-blue"
                  />
                </div>
                <button
                  type="submit"
                  disabled={savingLead}
                  data-testid="handoff-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-3 text-sm font-medium rounded-lg hover:brightness-105 transition-all disabled:opacity-60"
                >
                  {savingLead ? <Loader2 className="animate-spin" size={16} /> : <WhatsAppIcon size={17} />}
                  Continue to WhatsApp
                </button>
                <p className="text-[11px] text-charcoal/45 leading-relaxed">
                  We store your details only to respond to your enquiry. Prefer to message us yourself? Raman {CONTACTS.raman.phone} · Kriti {CONTACTS.kriti.phone}.
                </p>
              </form>
            </div>
          )}
        </div>
      )}
    </>
  );
}
